import type { ObjectSchema } from 'yup';
import * as yup from 'yup';

export type SpreadSheetCell = string | number;

export type SpreadSheetRow = SpreadSheetCell[];

export interface SpreadsheetStructure {
  headerRow: SpreadSheetRow;
  dataRows: SpreadSheetRow[];
}

export interface ImportColumnConfig<T extends string = string> {
  name: T;
  recognizedColumnNames: string[];
}

export interface ImportSheetConfig<T extends string = string> {
  columns: ImportColumnConfig<T>[];
  schema: ObjectSchema<any>;
}

export type ImportedItem = Record<string, string | number | boolean>;

export type ColumnErrorReason = 'missing' | 'requiredMissing' | 'invalid' | 'headerNotFound';

export type RowImportOk<T extends object, K extends string = Extract<keyof T, string>> = {
  status: 'ok';
  rowIndex: number; // 1-based data row number including header offset
  dataRow: SpreadSheetRow;
  values: T;
  warnings?: Array<{ column: K; message: string }>;
};

export type RowImportError<T extends object, K extends string = Extract<keyof T, string>> = {
  status: 'error';
  rowIndex: number;
  dataRow: SpreadSheetRow;
  values: Partial<T>;
  errors: Array<{
    column: K;
    reason: ColumnErrorReason;
    raw?: unknown;
  }>;
  rowErrors?: Array<{ message: string }>;
};

export type RowImportResult<T extends object, K extends string = Extract<keyof T, string>> =
  | RowImportOk<T, K>
  | RowImportError<T, K>;

export interface ImportRun<T extends object, K extends string = Extract<keyof T, string>> {
  file: File;
  headerRow: SpreadSheetRow;
  columnIndexMap: Record<K, number>; // -1 for missing
  missingRequiredColumns: K[];
  rows: RowImportResult<T, K>[];
  summary: {
    status: 'success' | 'error';
    totalCount: number;
    okCount: number;
    errorCount: number;
  };
}

export interface ImportState {
  run: ImportRun<ImportedItem, string> | undefined;
  importedFile: File | undefined;
  dataRowsRaw: SpreadSheetRow[] | undefined;

  importSpreadsheet: <T extends object, K extends string = Extract<keyof T, string>>(
    rows: SpreadSheetRow[],
    file: File,
    config: ImportSheetConfig<K>
  ) => ImportRun<T, K>;

  clearState: () => void;
}

export const spreadsheetNumber = () =>
  yup.number().transform((_value, originalValue) => {
    if (originalValue === null || originalValue === undefined || originalValue === '') {
      return undefined;
    }
    const s = String(originalValue).trim().replace(/\s/g, '').replace(',', '.');
    const n = Number(s);
    return Number.isNaN(n) ? NaN : n;
  });

export const spreadsheetDate = () =>
  yup.date().transform((_value, originalValue) => {
    if (!originalValue) return undefined;
    const d = new Date(originalValue + '');
    return isNaN(d.getTime()) ? new Date('invalid') : d;
  });

export const spreadsheetString = () =>
  yup.string().transform((_value, originalValue) => {
    if (originalValue === null || originalValue === undefined || originalValue === '') {
      return undefined;
    }
    return String(originalValue).trim();
  });

export const spreadsheetBoolean = () =>
  yup.boolean().transform((_value, originalValue) => {
    return Boolean(originalValue);
  });
